import { useState } from "react";

import { CommonDataSetExportApi } from "@/Administration/Api/CommonDataSetExport/CommonDataSetExportApi";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { Guid } from "@/Common/Models/Guid";
import { Person } from "@/Common/Models/Person/Person";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { Button } from "@/DesignSystem";
import { CategoriesSelection } from "./CategoriesSelection";
import { AvailableExportCategories } from "./ExportCategories";
import { PatientsSelection } from "./PatientsSelection";
import { ProviderSelection } from "./ProviderSelection";

import styles from "./OntarioCommonDataSetExportForm.module.scss";

interface IFormData {
    categoriesToExport: string[];
    providerContactFacetId: Guid;
    selectedPatients: Person[];
    allPatientsChecked: boolean;
}

function validateForm(data: IFormData): boolean {
    const hasCategorySelection = data.categoriesToExport?.any();
    const hasProviderContact = !GuidHelper.IsNullOrEmpty(data.providerContactFacetId);
    const hasPatientSelection = data.allPatientsChecked || data.selectedPatients?.any();

    return hasCategorySelection && hasProviderContact && hasPatientSelection;
}

function FormButtons(props: Readonly<{ onExport: () => void, onCancel: () => void, isFormValid: boolean, disabled: boolean }>): JSX.Element {
    const { getText } = useTranslation();

    return (
        <div className={styles.footerButtonsContainer}>
            <ButtonGroup>
                <Button
                    onClick={props.onCancel}
                    variant="neutral"
                    category="secondary"
                    dataCy="ontarioCommonDataSetExportForm_CancelButton"
                    label={getText("CancelLabel")}
                />
                <Button
                    onClick={props.onExport}
                    variant="brand"
                    category="primary"
                    dataCy="ontarioCommonDataSetExportForm_ExportButton"
                    label={getText("ExportLabel")}
                    disabled={!props.isFormValid}
                />
            </ButtonGroup>
        </div>
    );
}

function OntarioCommonDataSetExportForm(props: Readonly<{ onClose: () => void }>): JSX.Element {
    const [formState, setFormState] = useState<IFormData>({
        categoriesToExport: AvailableExportCategories.map(p => p.key),
        providerContactFacetId: GuidHelper.Empty(),
        selectedPatients: [],
        allPatientsChecked: true
    });

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const onExportButtonClick = async (): Promise<void> => {
        setIsLoading(true);
        const response = await CommonDataSetExportApi.createCommonDataSetExport({
            EmployeeId: formState.providerContactFacetId,
            Categories: formState.categoriesToExport,
            ExportFullPatientRoster: formState.allPatientsChecked,
            PatientIds: formState.selectedPatients.map(p => p.ContactFacetId)
        });
        setIsLoading(false);

        if (!response.isSuccess) {
            return sessionStore.showPopup(response.errors);
        }
        props.onClose();
        sessionStore.dispatch(LocalOperations.AdministrationLog.RefreshGrid);
    };

    return (
        <Modal
            onClose={props.onClose}
            showX
            width={800}
            title="CDS Export"
            footer={(
                <FormButtons
                    onCancel={props.onClose}
                    onExport={onExportButtonClick}
                    isFormValid={validateForm(formState)}
                    disabled={isLoading}
                />
            )}
        >
            <div className={styles.body}>
                <ProviderSelection
                    providerContactFacetId={formState.providerContactFacetId}
                    onChange={providerContactFacetId => setFormState(p => ({ ...p, providerContactFacetId }))}
                />
                <PatientsSelection
                    allChecked={formState.allPatientsChecked}
                    selectedPatients={formState.selectedPatients}
                    onAllChecked={allPatientsChecked => setFormState(p => ({ ...p, allPatientsChecked }))}
                    onChange={selectedPatients => setFormState(p => ({ ...p, selectedPatients }))}
                />
                <CategoriesSelection
                    selectedCategories={formState.categoriesToExport}
                    onChange={categoriesToExport => setFormState(p => ({ ...p, categoriesToExport }))}
                />
            </div>
        </Modal>
    );
}

export { OntarioCommonDataSetExportForm };
